import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

interface ImageViewerProps {
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
  altText?: string;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ 
  imageUrl, 
  isOpen, 
  onClose, 
  altText = 'Image' 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Device detection for optimizations (memoized)
  const deviceInfo = useMemo(() => ({
    isMobile: typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
    isIOS: typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent),
    isSafari: typeof window !== 'undefined' && /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    supportsWebP: typeof window !== 'undefined' && document.createElement('canvas').toDataURL('image/webp').indexOf('data:image/webp') === 0
  }), []);

  const { isMobile, isIOS, isSafari, supportsWebP } = deviceInfo;

  // Optimized image URL with format detection
  const optimizedImageUrl = useMemo(() => {
    if (!imageUrl) return '';
    
    // If image URL already has parameters, append format parameter
    const separator = imageUrl.includes('?') ? '&' : '?';
    const formatParam = supportsWebP ? 'format=webp' : 'format=jpeg';
    
    // For high-resolution images, add quality parameter to reduce file size
    return `${imageUrl}${separator}${formatParam}&q=85`;
  }, [imageUrl, supportsWebP]);

  // Reset loading states when image URL changes and preload image
  useEffect(() => {
    if (isOpen && optimizedImageUrl) {
      setImageLoaded(false);
      setImageError(false);
      setIsLoading(true);
      
      // Preload image for smoother UX
      const img = new Image();
      img.onload = () => {
        setImageLoaded(true);
        setIsLoading(false);
        setImageError(false);
      };
      img.onerror = () => {
        setImageLoaded(false);
        setIsLoading(false);
        setImageError(true);
      };
      img.src = optimizedImageUrl;
    }
  }, [optimizedImageUrl, isOpen]);

  // Optimized callback functions to prevent re-renders
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
    setIsLoading(false);
    setImageError(false);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoaded(false);
    setIsLoading(false);
    setImageError(true);
  }, []);

  // Optimized retry function with performance considerations
  const handleRetry = useCallback(() => {
    setImageError(false);
    setIsLoading(true);
    
    // Preload image in background for better UX
    const img = new Image();
    img.onload = handleImageLoad;
    img.onerror = handleImageError;
    
    // Use cache-busting parameter and optimized URL
    const retryUrl = optimizedImageUrl.includes('?') 
      ? `${optimizedImageUrl}&retry=${Date.now()}`
      : `${optimizedImageUrl}?retry=${Date.now()}`;
    
    img.src = retryUrl;
  }, [optimizedImageUrl, handleImageLoad, handleImageError]);

  // NEW APPROACH: Use a different portal strategy with body scroll lock
  useEffect(() => {
    if (!isOpen) return;

    // Prevent body scroll completely
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollBarWidth}px`;
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  // Keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Create the modal content with absolute viewport positioning
  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          zIndex: 1000001,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          border: 'none',
          borderRadius: '50%',
          padding: '10px',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '44px',
          height: '44px'
        }}
        aria-label="Close image viewer"
      >
        <XMarkIcon style={{ width: '24px', height: '24px' }} />
      </button>

      {/* Image container */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'calc(100vw - 40px)',
          height: 'calc(100vh - 40px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: '100vw',
          maxHeight: '100vh'
        }}
      >
          {/* Circular Loading Indicator - Cupertino Style */}
          {isLoading && !imageError && (
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{ zIndex: 1000000 }}
            >
              <div className="relative">
                {/* Circular progress spinner */}
                <svg
                  className="animate-spin"
                  width="60"
                  height="60"
                  viewBox="0 0 60 60"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Background circle */}
                  <circle
                    cx="30"
                    cy="30"
                    r="26"
                    stroke="rgba(255, 255, 255, 0.2)"
                    strokeWidth="4"
                    fill="none"
                  />
                  {/* Animated progress circle */}
                  <circle
                    cx="30"
                    cy="30"
                    r="26"
                    stroke="white"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="163"
                    strokeDashoffset="80"
                    style={{
                      transformOrigin: 'center',
                    }}
                  />
                </svg>
              </div>
            </div>
          )}
          
          {/* Error State */}
          {imageError && (
            <div 
              className="absolute inset-0 flex items-center justify-center"
              style={{ zIndex: 1000000 }}
            >
              <div className="flex flex-col items-center space-y-4 text-white">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-400" />
                <div className="text-center">
                  <h3 className="text-lg font-semibold mb-2">Failed to load image</h3>
                  <p className="text-base-content/50 text-sm mb-4">The image could not be loaded. Please try again.</p>
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}

          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            centerOnInit={true}
            wheel={{ 
              step: 0.1,
              disabled: isMobile, // Disable wheel on mobile to prevent conflicts
              wheelDisabled: false,
              touchPadDisabled: false
            }}
            pinch={{ 
              step: 0.1,
              disabled: !isMobile // Enable pinch only on mobile devices
            }}
            doubleClick={{
              step: 1.5,
              mode: 'toggle',
              animationTime: isMobile ? 200 : 300 // Faster animation for mobile
            }}
            limitToBounds={true}
            smooth={true}
            velocityAnimation={{
              sensitivity: isMobile ? 0.8 : 1, // Slightly reduced sensitivity on mobile
              animationTime: 400,
              animationType: 'easeOutQuart'
            }}
            // Enhanced touch handling for mobile devices
            panning={{
              velocityDisabled: false,
              lockAxisX: false,
              lockAxisY: false,
              activationKeys: [], // Disable keyboard panning for performance
              wheelPanning: !isMobile // Enable wheel panning only on desktop
            }}
            // Performance optimizations
            alignmentAnimation={{
              sizeX: 0,
              sizeY: 0,
              velocityAlignmentTime: 200
            }}
            disablePadding={true} // Reduce layout calculations
          >
            {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
              <>
                <TransformComponent
                  wrapperStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  contentStyle={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <img
                    src={optimizedImageUrl}
                    alt={altText}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className={`max-w-full max-h-full object-contain select-none transition-opacity duration-500 ease-in-out ${
                      imageLoaded && !imageError ? 'opacity-100' : 'opacity-0'
                    }`}
                    draggable={false}
                    loading="eager" // Force immediate loading for modal images
                    decoding="async" // Async decoding for better performance
                    style={{ 
                      userSelect: 'none',
                      touchAction: 'none',
                      willChange: 'transform', // Hint to browser for GPU acceleration
                      backfaceVisibility: 'hidden', // Prevent flickering during transforms
                      transform: 'translateZ(0)' // Force hardware acceleration
                    }}
                  />
                </TransformComponent>

                {/* Zoom controls for desktop - only show when image is loaded */}
                {imageLoaded && !imageError && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 gap-2 bg-black bg-opacity-50 rounded-full p-2 hidden md:flex">
                    <button
                      onClick={() => zoomOut()}
                      className="text-white hover:text-base-content/50 p-2 rounded-full hover:bg-base-100 hover:bg-opacity-20 transition-all duration-200"
                      aria-label="Zoom out"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <button
                      onClick={() => resetTransform()}
                      className="text-white hover:text-base-content/50 p-2 rounded-full hover:bg-base-100 hover:bg-opacity-20 transition-all duration-200"
                      aria-label="Reset zoom"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <button
                      onClick={() => zoomIn()}
                      className="text-white hover:text-base-content/50 p-2 rounded-full hover:bg-base-100 hover:bg-opacity-20 transition-all duration-200"
                      aria-label="Zoom in"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </TransformWrapper>
        </div>
      </div>
    );

  // Use portal to render directly to document.body for proper positioning
  if (typeof document === 'undefined') return null; // SSR safety check
  
  return createPortal(modalContent, document.body);
};

export default ImageViewer;